import { useState, useCallback, useEffect, useRef } from 'react';

import { CUSTOM_DATES_TITLE } from '~/shared/components/UtilizationCharts/dateFilterMenuItems';
import {
  dateToTimeParts,
  timePartToDate,
  pad,
  validateRanges,
  type TimeParts,
} from '~/shared/components/DateRangePopup/helpers';

interface UseCustomTimeRangeOptions {
  selectedStartDate: string;
  selectedEndDate: string;
  setTimeRangeTitle: (title: string) => void;
}

interface UseCustomTimeRangeReturn {
  startTimeParts: TimeParts;
  endTimeParts: TimeParts;
  customTimeError: string;
  customRangeError: string | undefined;
  patchStartTimeParts: (next: TimeParts) => void;
  patchEndTimeParts: (next: TimeParts) => void;
  setStartTimeParts: (v: TimeParts) => void;
  setEndTimeParts: (v: TimeParts) => void;
  resetErrors: () => void;
}

export function useCustomTimeRange({
  selectedStartDate,
  selectedEndDate,
  setTimeRangeTitle,
}: UseCustomTimeRangeOptions): UseCustomTimeRangeReturn {
  const setTimeRangeTitleRef = useRef(setTimeRangeTitle);
  const [startTimeParts, setStartTimeParts] = useState<TimeParts>({
    hours: '',
    minutes: '',
    isPm: false,
  });
  const [endTimeParts, setEndTimeParts] = useState<TimeParts>({
    hours: '',
    minutes: '',
    isPm: false,
  });
  const [customTimeError, setCustomTimeError] = useState('');
  const [customRangeError, setCustomRangeError] = useState<string | undefined>();

  const resetErrors = useCallback(() => {
    setCustomTimeError('');
    setCustomRangeError(undefined);
  }, []);

  useEffect(() => {
    setTimeRangeTitleRef.current = setTimeRangeTitle;
  }, [setTimeRangeTitle]);

  const tryCommitCustomTimes = useCallback(
    (nextStart: TimeParts, nextEnd: TimeParts) => {
      const hS = parseInt(nextStart.hours, 10);
      const mS = parseInt(nextStart.minutes, 10);
      const hE = parseInt(nextEnd.hours, 10);
      const mE = parseInt(nextEnd.minutes, 10);

      const errors: string[] = [];
      if (isNaN(hS) || hS < 1 || hS > 12) errors.push('Start: hours must be between 1 and 12');
      if (isNaN(mS) || mS < 0 || mS > 59) errors.push('Start: minutes must be between 0 and 59');
      if (isNaN(hE) || hE < 1 || hE > 12) errors.push('End: hours must be between 1 and 12');
      if (isNaN(mE) || mE < 0 || mE > 59) errors.push('End: minutes must be between 0 and 59');

      const localErr = errors.join('; ');
      setCustomTimeError(localErr);
      if (localErr) {
        setCustomRangeError(undefined);
        return;
      }

      const paddedStart = { ...nextStart, hours: pad(nextStart.hours), minutes: pad(nextStart.minutes) };
      const paddedEnd = { ...nextEnd, hours: pad(nextEnd.hours), minutes: pad(nextEnd.minutes) };
      const start = timePartToDate(new Date(selectedStartDate), paddedStart);
      const end = timePartToDate(new Date(selectedEndDate), paddedEnd);
      const rangeErr = validateRanges(start, end);
      setCustomRangeError(rangeErr);
      if (rangeErr) return;

      setTimeRangeTitleRef.current(`${CUSTOM_DATES_TITLE}|${start.toISOString()}|${end.toISOString()}`);
    },
    [selectedStartDate, selectedEndDate]
  );

  const patchStartTimeParts = useCallback(
    (next: TimeParts) => {
      setStartTimeParts(next);
      setEndTimeParts((e) => {
        tryCommitCustomTimes(next, e);
        return e;
      });
    },
    [tryCommitCustomTimes]
  );

  const patchEndTimeParts = useCallback(
    (next: TimeParts) => {
      setEndTimeParts(next);
      setStartTimeParts((s) => {
        tryCommitCustomTimes(s, next);
        return s;
      });
    },
    [tryCommitCustomTimes]
  );

  return {
    startTimeParts,
    endTimeParts,
    customTimeError,
    customRangeError,
    patchStartTimeParts,
    patchEndTimeParts,
    setStartTimeParts,
    setEndTimeParts,
    resetErrors,
  };
}
